import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const SiteContext = createContext({ settings: null, homepage: null, about: null, loading: true, error: null });
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Minimal JS-side fallback so pages render even if the API is briefly
// unreachable during first paint. Real content still comes from /api on
// success; this is defensive UX only, matching backend defaults in shape.
const FALLBACK_SETTINGS = { publisher_name: "Glories of India Publications" };
const FALLBACK_HOMEPAGE = { sections: [
  { key: "hero", enabled: true, order: 0, heading: "Glories of India Publications", subheading: "" },
] };
const FALLBACK_ABOUT = { eyebrow: "About the Author", role_line: "", bio: "", journey: { nodes: [] }, environmental: { orgs: [] }, gallery: { images: [] }, invite: {} };

const fetchWithRetry = async (url, retries = 1) => {
  try {
    const r = await axios.get(url, { timeout: 8000 });
    return r.data;
  } catch (e) {
    if (retries > 0) {
      await new Promise(res => setTimeout(res, 1000));
      return fetchWithRetry(url, retries - 1);
    }
    throw e;
  }
};

export const SiteProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [homepage, setHomepage] = useState(null);
  const [about, setAbout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    const results = await Promise.allSettled([
      fetchWithRetry(`${BACKEND_URL}/api/site/settings`),
      fetchWithRetry(`${BACKEND_URL}/api/site/homepage`),
      fetchWithRetry(`${BACKEND_URL}/api/site/about`),
    ]);
    setSettings(results[0].status === "fulfilled" ? results[0].value : FALLBACK_SETTINGS);
    setHomepage(results[1].status === "fulfilled" ? results[1].value : FALLBACK_HOMEPAGE);
    setAbout(results[2].status === "fulfilled" ? results[2].value : FALLBACK_ABOUT);
    const failed = results.filter(r => r.status === "rejected").length;
    if (failed) setError(`${failed} site config request(s) failed — showing safe defaults.`);
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const value = useMemo(
    () => ({ settings, homepage, about, loading, error, refresh }),
    [settings, homepage, about, loading, error, refresh],
  );
  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
};

export const useSite = () => useContext(SiteContext);
export const useSiteSettings = () => useContext(SiteContext).settings;
export const useHomepage = () => useContext(SiteContext).homepage;
export const useAbout = () => useContext(SiteContext).about;
